/** Yalıda Davet · zod validation schemas shared by API and forms. */
import { z } from 'zod';
import { PLATFORM_MAX_GUESTS, HARD_CHECKOUT_DEADLINE } from './rules.js';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD bekleniyor');
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM bekleniyor');

export const searchFiltersSchema = z.object({
  q: z.string().trim().max(120).optional(),
  city: z.string().trim().max(60).optional(),
  district: z.string().trim().max(60).optional(),
  propertyType: z.enum(['yali', 'villa', 'kosk', 'bahce', 'cati', 'tekne']).optional(),
  checkin: isoDate.optional(),
  checkout: isoDate.optional(),
  guestCount: z.coerce.number().int().min(1).max(PLATFORM_MAX_GUESTS).optional(),
  eventTypeId: z.string().max(40).optional(),
  minPriceKurus: z.coerce.number().int().min(0).optional(),
  maxPriceKurus: z.coerce.number().int().min(0).optional(),
  minAreaM2: z.coerce.number().int().min(0).optional(),
  maxAreaM2: z.coerce.number().int().min(0).optional(),
  hasPool: z.coerce.boolean().optional(),
  hasGarden: z.coerce.boolean().optional(),
  hasSeaView: z.coerce.boolean().optional(),
  hasParking: z.coerce.boolean().optional(),
  sort: z.enum(['price_asc', 'price_desc', 'rating_desc', 'capacity_desc', 'garden_desc', 'newest']).default('price_asc'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(60).default(12),
});
export type SearchFiltersInput = z.infer<typeof searchFiltersSchema>;

export const createMansionSchema = z
  .object({
    title: z.string().trim().min(8, 'Başlık en az 8 karakter olmalı').max(140),
    description: z.string().trim().max(5000).default(''),
    propertyType: z.enum(['yali', 'villa', 'kosk', 'bahce', 'cati', 'tekne']).default('yali'),
    city: z.string().trim().min(2),
    district: z.string().trim().min(2),
    neighborhood: z.string().trim().max(80).optional(),
    addressLine: z.string().trim().max(240).optional(),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),

    totalAreaM2: z.coerce.number().int().min(1).max(100000),
    gardenSizeM2: z.coerce.number().int().min(0).max(100000),
    indoorAreaM2: z.coerce.number().int().min(0).max(100000).default(0),
    maxCapacity: z.coerce.number().int().min(1).max(PLATFORM_MAX_GUESTS),
    seatedCapacity: z.coerce.number().int().min(0).optional(),
    bedrooms: z.coerce.number().int().min(0).max(60).default(0),
    bathrooms: z.coerce.number().int().min(0).max(60).default(0),

    dailyPriceKurus: z.coerce.number().int().min(0),
    weekendPriceKurus: z.coerce.number().int().min(0),
    cleaningFeeKurus: z.coerce.number().int().min(0).default(0),
    minNights: z.coerce.number().int().min(1).default(1),

    hasPool: z.boolean().default(false),
    hasGarden: z.boolean().default(false),
    hasSeaView: z.boolean().default(false),
    hasParking: z.boolean().default(false),
    parkingCapacity: z.coerce.number().int().min(0).default(0),
    hasWifi: z.boolean().default(true),
    hasGenerator: z.boolean().default(false),
    hasSoundSystem: z.boolean().default(false),
    isAccessible: z.boolean().default(false),

    hasStagingArea: z.boolean().default(false),
    stagingAreaM2: z.coerce.number().int().min(0).default(0),
    hasCateringArea: z.boolean().default(false),
    cateringAreaM2: z.coerce.number().int().min(0).default(0),
    hasMusicLicence: z.boolean().default(false),

    isResidentialZone: z.boolean().default(true),
    checkinTime: time.default('14:00'),
    checkoutDeadline: time.default(HARD_CHECKOUT_DEADLINE),
    noiseCurfewTime: time.default('23:00'),
    neighborhoodNotes: z.string().max(1000).optional(),
    houseRules: z.string().max(3000).optional(),

    eventTypeIds: z.array(z.string()).default([]),
    photoIds: z.array(z.string()).default([]),
  })
  .superRefine((v, ctx) => {
    if (v.gardenSizeM2 > 0 && !v.hasGarden) {
      ctx.addIssue({ code: 'custom', path: ['hasGarden'], message: 'Bahçe alanı girdiniz; "Bahçeli" seçeneği işaretlenmeli.' });
    }
    if (v.checkoutDeadline > HARD_CHECKOUT_DEADLINE) {
      ctx.addIssue({ code: 'custom', path: ['checkoutDeadline'], message: `Çıkış saati en geç ${HARD_CHECKOUT_DEADLINE} olabilir (gürültü yönetmeliği).` });
    }
    if (v.weekendPriceKurus < v.dailyPriceKurus) {
      ctx.addIssue({ code: 'custom', path: ['weekendPriceKurus'], message: 'Hafta sonu fiyatı günlük fiyattan düşük olamaz.' });
    }
    const weddingish = v.eventTypeIds.some((id) => ['dugun', 'nikah', 'nisan'].includes(id));
    if (weddingish && !v.hasStagingArea) {
      ctx.addIssue({ code: 'custom', path: ['hasStagingArea'], message: 'Düğün/Nikah/Nişan için sahne-kurulum alanı zorunludur.' });
    }
    if (v.eventTypeIds.includes('dugun') && !v.hasCateringArea) {
      ctx.addIssue({ code: 'custom', path: ['hasCateringArea'], message: 'Düğün için catering alanı zorunludur.' });
    }
  });
export type CreateMansionInput = z.infer<typeof createMansionSchema>;

export const updateMansionSchema = createMansionSchema.innerType().partial();

export const quoteSchema = z.object({
  mansionId: z.string().min(1),
  checkin: isoDate,
  checkout: isoDate,
  guestCount: z.coerce.number().int().min(1).max(PLATFORM_MAX_GUESTS),
  eventTypeId: z.string().min(1),
  addonIds: z.array(z.string()).default([]),
});

export const createBookingSchema = quoteSchema.extend({
  guestNote: z.string().max(1000).optional(),
  noisePolicyAccepted: z.literal(true, { errorMap: () => ({ message: 'Gürültü yönetmeliğini onaylamanız gerekir.' }) }),
  houseRulesAccepted: z.literal(true, { errorMap: () => ({ message: 'Ev kurallarını onaylamanız gerekir.' }) }),
});
export type CreateBookingInput = z.infer<typeof createBookingSchema>;

export const sendMessageSchema = z.object({
  body: z.string().trim().max(4000),
  messageType: z.enum(['text', 'image', 'file']).default('text'),
  attachmentR2Key: z.string().optional(),
  attachmentName: z.string().optional(),
  attachmentSize: z.number().int().optional(),
}).refine((v) => v.body.length > 0 || !!v.attachmentR2Key, { message: 'Boş mesaj gönderilemez.' });

export const startConversationSchema = z.object({
  mansionId: z.string().min(1),
  body: z.string().trim().min(1).max(4000),
});
